import { REPORT_CARD_STATUS_FILTERS } from '../../ReportCardAccess/report-card-access.constants';
import { ReportCardStatusFilter } from '../../ReportCardAccess/report-card-access.types';

export type GoalieFilter = 'goalie' | 'other';

export interface IRankingFilters {
  positions: string[];
  levels: string[];
  teams: string[];
  statusFilters: ReportCardStatusFilter[];
  // The rows a name search matched, or null when there is no search.
  nameMatches: Set<IPlayerAssessment> | null;
  includeConcluded: boolean;
}

const isGoalie = (playerAssessment: IPlayerAssessment) => playerAssessment.position?.name?.toLowerCase() === 'goalie';

export const filterByGoalie = (playerAssessments: IPlayerAssessment[], goalieFilter: GoalieFilter) =>
  playerAssessments.filter(playerAssessment => isGoalie(playerAssessment) === (goalieFilter === 'goalie'));

const matchesAny = <T>(selected: T[], value: T) => !selected.length || selected.includes(value);

const statusesFor = (statusFilters: ReportCardStatusFilter[]): ReportCardAccessStatus[] =>
  REPORT_CARD_STATUS_FILTERS.filter(({ filter }) => statusFilters.includes(filter)).flatMap(({ statuses }) => statuses);

// Applies every filter in ranking order. `hiddenConcludedCount` counts the rows that matched every
// other filter but are hidden only because "Show Concluded" is off, so the page can say so.
export const filterRankingPlayerAssessments = (playerAssessments: IPlayerAssessment[], filters: IRankingFilters) => {
  const statuses = statusesFor(filters.statusFilters);
  const matched = playerAssessments.filter(playerAssessment => (
    matchesAny(filters.positions, playerAssessment.position?.name)
    && matchesAny(filters.levels, playerAssessment.level)
    && matchesAny(filters.teams, playerAssessment.team)
    && (!statuses.length || (!!playerAssessment.reportCardAccess && statuses.includes(playerAssessment.reportCardAccess.status)))
    && (!filters.nameMatches || filters.nameMatches.has(playerAssessment))
  ));

  if (filters.includeConcluded) return { shown: matched, hiddenConcludedCount: 0 };
  const shown = matched.filter(playerAssessment => !playerAssessment.isConcluded);
  return { shown, hiddenConcludedCount: matched.length - shown.length };
};

export const countReportCardStatusFilters = (playerAssessments: IPlayerAssessment[]): Record<ReportCardStatusFilter, number> => {
  const counts: Record<ReportCardStatusFilter, number> = { AVAILABLE: 0, NOT_RELEASED: 0, REVOKED: 0, NO_DATA: 0 };
  playerAssessments.forEach(({ reportCardAccess }) => {
    const option = reportCardAccess && REPORT_CARD_STATUS_FILTERS.find(({ statuses }) => statuses.includes(reportCardAccess.status));
    if (option) counts[option.filter] += 1;
  });
  return counts;
};
